import { FilterKindEnum } from '../../../domains/global-filters/model/FilterChain';
import type { ExperimentType } from '../../../components/StatelessComponent/SingleFilter/ExperimentFilter/ExperimentFilterType';
import type { IntermediateFormat } from '../../../domains/global-filters/model/IntermediateFormat';
import type { QueryFormatFilter } from '../../../domains/global-filters/model/QueryFormat';
import {
  QueryListOperandEnum,
  QueryMapOperandEnum,
  QueryKeyEnum,
} from '../../../domains/global-filters/model/QueryFormat';

export const GOOGLE_OPTIMIZE_PREFIX = 'exp.go';

export const OPTIMIZELY_PREFIX = 'exp.opt';

export const UNBOUNCE_PREFIX = 'exp.ub';

export function fromUiToQueryFormat(
  uiValue?: ExperimentType,
  filterKind?: FilterKindEnum,
): QueryFormatFilter {
  if (!uiValue) {
    return {};
  }
  let prefix: string;
  if (filterKind === FilterKindEnum.GoogleOptimize) {
    prefix = GOOGLE_OPTIMIZE_PREFIX;
  } else if (filterKind === FilterKindEnum.Optimizely) {
    prefix = OPTIMIZELY_PREFIX;
  } else if (filterKind === FilterKindEnum.Unbounce) {
    prefix = UNBOUNCE_PREFIX;
  } else {
    return {};
  }
  const experimentString = uiValue.experimentId ? `.e:${uiValue.experimentId}` : '';
  const containerString = uiValue.containerId ? `.c:${uiValue.containerId}` : '';
  if (uiValue.selectedVariants != null && uiValue.selectedVariants.length > 0) {
    const list = uiValue.selectedVariants.map((item) => {
      return {
        [QueryMapOperandEnum.EQUAL]: {
          [QueryKeyEnum.experiments]: `${prefix}${experimentString}.v:${item}${containerString}`,
        },
      };
    });

    return list.length > 1 ? { [QueryListOperandEnum.OR]: list } : list[0];
  }
  return {
    [QueryMapOperandEnum.EQUAL]: {
      [QueryKeyEnum.experiments]: `${prefix}${experimentString}${containerString}`,
    },
  };
}

export function fromIntermediateToUiFormat(intermediateValue: IntermediateFormat): ExperimentType {
  if (intermediateValue == null) {
    return {
      experimentId: '',
      containerId: '',
      selectedVariants: [],
    };
  }
  const uiFormat: ExperimentType = {
    experimentId: '',
    containerId: '',
    selectedVariants: [],
  };

  const EXPERIMENT_ID_REGEX = /.e:(?<id>.*?)(.c:|.e:|.v:|$)/;
  const CONTAINER_ID_REGEX = /.c:(?<id>.*?)(.c:|.e:|.v:|$)/;
  const VARIANT_ID_REGEX = /.v:(?<id>.*?)(.c:|.e:|.v:|$)/;

  /**
   * Experiment autotags is a combination of 'exp.go' plus experiment (e:...), optionally variant
   * (v:...), and a container ID (c:...).
   *
   * We loop through the various parts to pick up the relevant values for each part.
   *
   * Examples:
   * "exp.go.e:eony_FsmTQWOgP8UIwizhQ.c:GTM-P535R7H"] <- Without a variant
   * "exp.go.e:eony_FsmTQWOgP8UIwizhQ.v:0.c:GTM-P535R7H"] <- With a variant
   */
  (intermediateValue.value as string[]).forEach((item) => {
    const expId = EXPERIMENT_ID_REGEX.exec(item)?.groups?.id;
    const optionalContainer = CONTAINER_ID_REGEX.exec(item)?.groups?.id;
    const optionalVariant = VARIANT_ID_REGEX.exec(item)?.groups?.id;

    if (expId == null) {
      throw new Error(`Cannot parse experiment autotag: ${item}`);
    } else {
      uiFormat.experimentId = expId;
    }
    if (optionalVariant != null) {
      uiFormat.selectedVariants.push(optionalVariant);
    }
    if (optionalContainer != null) {
      uiFormat.containerId = optionalContainer;
    }
  });

  return uiFormat;
}
